import React, { useEffect, useState } from "react";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { KeyRound, Ticket } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import PasswordStrength from "@/components/PasswordStrength";
import { toast } from "sonner";
import { BrandHomeLink } from "@/components/BrandLogo";
import ThemeToggle from "@/components/ThemeToggle";

export default function Register() {
  const { user, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState(params.get("invite") || "");
  const [requiresInvite, setRequiresInvite] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/auth/registration-mode")
      .then(({ data }) => setRequiresInvite(!!data.requires_invite))
      .catch(() => setRequiresInvite(true));
  }, []);

  if (user && user !== false) return <Navigate to="/vault" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Master password must be at least 8 characters.");
      return;
    }
    if (requiresInvite && !inviteCode.trim()) {
      toast.error("An invite code is required to join this vault.");
      return;
    }
    setSubmitting(true);
    const r = await register(email, password, name, inviteCode.trim());
    setSubmitting(false);
    if (r.ok) {
      toast.success("Vault created");
      nav("/vault");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="min-h-screen flex relative" style={{ background: "var(--vault-app-bg)" }}>
      <ThemeToggle className="absolute top-5 right-5 z-10" testid="register-theme-toggle" />
      <div className="hidden lg:flex w-1/2 flex-col justify-between p-12" style={{ background: "var(--vault-sidebar-bg)" }}>
        <BrandHomeLink size="lg" testid="register-side-brand-logo" linkTestid="register-brand-home-link" />
        <div>
          <img
            src="https://static.prod-images.emergentagent.com/jobs/649c5d98-ab9a-4383-a42c-cabe200e9bef/images/ab77f75dca5c8795f454531ad9c4f1466856e9cbb476ca51871f5b4a26f7806c.png"
            alt="Secure vault"
            className="w-full max-w-md mx-auto"
          />
        </div>
        <div className="max-w-md">
          <h2 className="font-[Outfit] text-3xl tracking-tight mb-3">Built for the people you love.</h2>
          <p className="text-[color:var(--vault-text-secondary)] text-sm leading-relaxed">
            Pick a strong master password — it&apos;s the one key you&apos;ll always need to remember.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <img
              src="/ezguard-logo-full.webp"
              alt="EZGuard"
              className="hero-logo w-44 sm:w-52 h-auto mx-auto mb-8 select-none"
              draggable={false}
              data-testid="register-hero-logo"
            />
            <h1 className="font-[Outfit] text-4xl tracking-tight font-semibold">Create your vault</h1>
            <p className="text-[color:var(--vault-text-secondary)] text-sm mt-2">
              {requiresInvite
                ? "EZGuard is invite-only. Enter the code you were sent to join your family's vault."
                : "You're setting up the vault — this first account is the owner."}
            </p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            {requiresInvite && (
              <div>
                <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Invite code</Label>
                <div className="relative mt-1">
                  <Ticket size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--vault-text-tertiary)]" />
                  <Input
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    required
                    placeholder="ABCD-EFGH"
                    className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] pl-9 font-mono tracking-wider uppercase"
                    data-testid="register-invite-input"
                  />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Your name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Jordan"
                className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Master password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1 font-mono"
                data-testid="register-password-input"
              />
              <div className="mt-2">
                <PasswordStrength value={password} />
              </div>
              <p className="text-xs text-[color:var(--vault-text-tertiary)] mt-2">
                Make it long and memorable. If you lose this, you lose your vault.
              </p>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white"
              data-testid="register-submit-btn"
            >
              <KeyRound size={16} className="mr-2" />
              {submitting ? "Creating…" : "Create vault"}
            </Button>
          </form>
          <p className="text-sm text-[color:var(--vault-text-tertiary)] mt-6">
            Already have a vault?{" "}
            <Link to="/login" className="text-[color:var(--vault-brand)] font-medium hover:underline" data-testid="link-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
